<script lang="ts">
    import Vue from "vue";
    import { Component } from 'vue-facing-decorator';
    import { AlternativeEditorReadModel } from "@Alternatives/Alternatives/Domain/readmodels";
    import TextInputSingleValue from "@Alternatives/Alternatives/Components/FormFields/TextInputSingleValue.vue";
    import TextInputMultiValue from "@Alternatives/Alternatives/Components/FormFields/TextInputMultiValue.vue";
    import TextListSingleValue from "@Alternatives/Alternatives/Components/FormFields/TextListSingleValue.vue";
    import TextListMultiValue from "@Alternatives/Alternatives/Components/FormFields/TextListMultiValue.vue";
    import TextMultilineSingleValue from "@Alternatives/Alternatives/Components/FormFields/TextMultilineSingleValue.vue";
    import NumberInputSingleValue from "@Alternatives/Alternatives/Components/FormFields/NumberInputSingleValue.vue";
    import NumberInputMultiValue from "@Alternatives/Alternatives/Components/FormFields/NumberInputMultiValue.vue";
    import NumberListSingleValue from "@Alternatives/Alternatives/Components/FormFields/NumberListSingleValue.vue";
    import NumberListMultiValue from "@Alternatives/Alternatives/Components/FormFields/NumberListMultiValue.vue";
    import NumberCalculatedSingleValue from "@Alternatives/Alternatives/Components/FormFields/NumberCalculatedSingleValue.vue";
    import EmailInputSingleValue from "@Alternatives/Alternatives/Components/FormFields/EmailInputSingleValue.vue";
    import EmailInputMultiValue from "@Alternatives/Alternatives/Components/FormFields/EmailInputMultiValue.vue";
    import EmailListSingleValue from "@Alternatives/Alternatives/Components/FormFields/EmailListSingleValue.vue";
    import EmailListMultiValue from "@Alternatives/Alternatives/Components/FormFields/EmailListMultiValue.vue";
    import DateInputSingleValue from "@Alternatives/Alternatives/Components/FormFields/DateInputSingleValue.vue";
    import DateInputMultiValue from "@Alternatives/Alternatives/Components/FormFields/DateInputMultiValue.vue";
    import DateListSingleValue from "@Alternatives/Alternatives/Components/FormFields/DateListSingleValue.vue";
    import DateListMultiValue from "@Alternatives/Alternatives/Components/FormFields/DateListMultiValue.vue";
    import { AlternativeAttributeValueDTO } from "@Alternatives/Model";
    import { ValidationError } from "@common/types";

    @Component({
        components:
        {
            TextInputSingleValue, TextInputMultiValue, TextListSingleValue, TextListMultiValue, TextMultilineSingleValue,
            NumberInputSingleValue, NumberInputMultiValue, NumberListSingleValue, NumberListMultiValue, NumberCalculatedSingleValue,
            EmailInputSingleValue, EmailInputMultiValue, EmailListSingleValue, EmailListMultiValue,
            DateInputSingleValue, DateInputMultiValue, DateListSingleValue, DateListMultiValue
        },
        props: {
            propRm: { type: Object, required: true },
            propFormId: { type: String, required: true },
            propErrors: {type: Array, required: true}
        }
    })
    export default class AlternativeEditor extends Vue
    {
        get rm(): AlternativeEditorReadModel { return this.$props.propRm as AlternativeEditorReadModel; }
        get formId(): string { return this.$props.propFormId as string; }
        get errors(): ValidationError[] { return this.$props.propErrors as ValidationError[]; }
        
        get nameError()  { return this.errors?.find(error => error.Context.startsWith("Alternative:") && error.ErrorCode.startsWith("Name."))?.Error; }

        formSubmit()
        {
            const name = this.rm.AlternativeName;
            const description = this.rm.AlternativeDescription;
            const statusId = this.rm.AlternativeStatusId;

            const attributeValues = this.rm.Sections.flatMap(s => s.Attributes).flatMap(attr => 
            {
                if (attr.Value != null)
                {
                    const attrValue = new AlternativeAttributeValueDTO();
                    attrValue.AttributeId = attr.Id;
                    attrValue.Value = attr.Value;
                    return [attrValue];
                }
                else
                {
                    return attr.Values.filter(v => v != null).map(v => 
                    {
                        const attrValue = new AlternativeAttributeValueDTO();
                        attrValue.AttributeId = attr.Id;
                        attrValue.Value = v;
                        return attrValue;
                    });
                }
            });

            //console.log(attributeValues);

            this.$emit('form-submit', name, description, statusId, attributeValues);
        }
    }
</script>

<template>
<div>
    <form @submit.prevent="formSubmit" :id="formId">
        <p v-if="rm.FormBlurb" class="lead">{{rm.FormBlurb}}</p>

        <div class="form-group">
            <label><span style="color: red;">*</span> {{rm.AlternativeLabel}} Name</label>
            <input type="text" v-model.trim="rm.AlternativeName" class="form-control" :readonly="rm.IsNameReadonly" required>
            <div class="error">{{nameError}}</div>
        </div>

        <div v-if="rm.IsDescriptionVisible" class="form-group">
            <label>Description</label>
            <textarea v-model.trim="rm.AlternativeDescription" class="form-control" :readonly="rm.IsDescriptionReadonly"></textarea>
        </div>

        <div v-if="rm.IsStatusVisible" class="form-group">
            <label>Status:</label>
            <select class="form-control" v-model="rm.AlternativeStatusId" :readonly="rm.IsStatusReadonly" :disabled="rm.IsStatusReadonly">
                <option v-for="s in rm.Statuses" :value="s.Id">{{s.Name}}</option>
            </select>
        </div>

        <div v-for="s in rm.Sections" :key="s.Name">
            <h5 v-if="s.Name">
                <a class="text-primary" @click="s.IsExpanded = !s.IsExpanded">
                    <i class="fa fa-angle-right" :class="{'fa-angle-down': s.Expanded}"></i> {{s.Name}}
                </a>
            </h5>
            <div v-if="s.IsExpanded">
                <div v-for="f in s.Attributes" :key="f.Id">
                    <DateInputSingleValue v-if="f.FieldType === 'DateInputSingleValue'" :propF="f" :propErrors="errors" />
                    <DateInputMultiValue v-if="f.FieldType === 'DateInputMultiValue'" :propF="f" :propErrors="errors" />
                    <DateListSingleValue v-if="f.FieldType === 'DateListSingleValue'" :propF="f" :propErrors="errors" />
                    <DateListMultiValue v-if="f.FieldType === 'DateListMultiValue'" :propF="f" :propErrors="errors" />

                    <EmailInputSingleValue v-if="f.FieldType === 'EmailInputSingleValue'" :propF="f"  :propErrors="errors" />
                    <EmailInputMultiValue v-if="f.FieldType === 'EmailInputMultiValue'" :propF="f" :propErrors="errors" />
                    <EmailListSingleValue v-if="f.FieldType === 'EmailListSingleValue'" :propF="f" :propErrors="errors" />
                    <EmailListMultiValue v-if="f.FieldType === 'EmailListMultiValue'" :propF="f" :propErrors="errors" />

                    <NumberInputSingleValue v-if="f.FieldType === 'NumberInputSingleValue'" :propF="f" :propErrors="errors" />
                    <NumberInputMultiValue v-if="f.FieldType === 'NumberInputMultiValue'" :propF="f" :propErrors="errors" />
                    <NumberListSingleValue v-if="f.FieldType === 'NumberListSingleValue'" :propF="f" :propErrors="errors" />
                    <NumberListMultiValue v-if="f.FieldType === 'NumberListMultiValue'" :propF="f" :propErrors="errors" />
                    <NumberCalculatedSingleValue v-if="f.FieldType === 'NumberCalculatedSingleValue'" :propF="f" :propErrors="errors" />

                    <TextInputSingleValue v-if="f.FieldType === 'TextInputSingleValue'" :propF="f" :propErrors="errors" />
                    <TextInputMultiValue v-if="f.FieldType === 'TextInputMultiValue'" :propF="f" :propErrors="errors" />
                    <TextListSingleValue v-if="f.FieldType === 'TextListSingleValue'" :propF="f" :propErrors="errors" />
                    <TextListMultiValue v-if="f.FieldType === 'TextListMultiValue'" :propF="f" :propErrors="errors" />
                    <TextMultilineSingleValue v-if="f.FieldType === 'TextMultilineSingleValue'" :propF="f" :propErrors="errors" />
                </div>
            </div>
        </div>
    </form>
</div>
</template>